// REQ-088 (Fase F, Tarea F3) — Tab "Líderes": listado agregado de TODOS los líderes del scope
// (persona, no OU), con tamaño de equipo DIRECTO (sin cascadear la jerarquía) y reconocimientos por
// líder y por equipo (GET v1/analytics/recognition/leaders-list). Reemplaza la granularidad
// OU-based de la fase anterior (`useRecognitionLeaders`/`GET .../leaders`, REQ-088 fase 1) — ese
// endpoint SIGUE EXISTIENDO en el backend sin tocar, solo deja de tener consumidor en el front.
//
// `liderNombre`/`ouNombre` YA VIENEN RESUELTOS por el backend (F2a los resuelve vía
// UserAdminService): a diferencia del tab OU-based anterior, la TABLA no necesita
// `usePersonNameResolver` ni el árbol de OUs (`useOUItems`). El helper `nivelDeOu` que calculaba
// profundidad de OU (no de persona, no aplicaba a este dato) se eliminó junto con su único call
// site. La TABLA en pantalla NO depende de `row.nivel`: Fase G (REQ-088) pinta "Nivel" desde la
// profundidad real del árbol de jerarquía (`buildLeaderForest`, ver el comentario junto a
// `usePaginacion` más abajo) — pero el EXCEL exportado sí usa `row.nivel` (formateado "N-{n}" por
// `formatNivel`), porque F2a (UserAdminService) SÍ lo calcula ahora (`AncestorIds.Count + 1`,
// profundidad REAL en toda la empresa, no solo entre los líderes visibles en este listado como
// hace el árbol en pantalla).
//
// Punto 6 (revisión final, 2026-09-03) — `usePersonNameResolver`/`useRecognitionCatalog` SÍ hacen
// falta para «Filtros aplicados» del export (`conFiltrosDeTira`): la persona elegida en la tira
// puede no aparecer como fila (`liderId`) de este listado, y el Fundamental no viaja resuelto en
// ninguna fila — a diferencia de `liderNombre`/`ouNombre`, que sí.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecognitionLeadersList, useRecognitionCatalog, getFundamentalName, recognitionReportsApi } from '@/api/recognition';
import type { RecognitionReportParams, LeaderAggregateRowDto } from '@/api/recognition';
import { useOuNameResolver, usePersonNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { exportarLibro } from '../exportarLibro';
import { ReportStatus, conFiltrosDeTira } from '../reportsShared';
import { buildLeadersExcelSheets } from './leadersExcel';
import { Paginacion, usePaginacion } from '../Paginacion';
import { buildLeaderForest } from './leadersTree';
import { LeaderAvatar } from './LeaderAvatar';
import { DEFAULT_FILTERS } from '@/views/AnalyticsView/hooks/useAnalyticsFilters';

const SIN_DATO = '—';

/**
 * LID-D2 (REPORTERIA-REGLAS §11, War Room 2026-09-21 Rubi 1:41:12, Adriana 1:42:08) — qué recorte se
 * está viendo, DENTRO de la pestaña y encima de los datos: el periodo por omisión es la semana en curso
 * y quien entra no lo nota: el reporte parece vacío o corto y no sabe por qué.
 *
 * LID-D1 — y la aclaración que la reunión pidió como leyenda: fechas y Fundamental SÍ filtran los
 * reconocimientos (Adriana 1:40:02); lo que no cambia con ellos es el tamaño de equipo, que sale del
 * organigrama. Declarar esos filtros «no aplica» sería falso (decisión del usuario, 2026-09-29).
 */
function textoDelRecorte(
  params: RecognitionReportParams,
  ouNombresSeleccionados: string | null | undefined,
  nombreDeFundamental: (id: string) => string,
  resolvePersonName: (id: string) => string,
  t: (clave: string, opciones?: Record<string, unknown>) => string,
): string {
  const rango = [params.startDate, params.endDate].filter(Boolean).join(' – ');
  const esSemanaEnCurso = params.startDate === DEFAULT_FILTERS.dateFrom && params.endDate === DEFAULT_FILTERS.dateTo;
  const partes = [
    ouNombresSeleccionados?.trim() ? ouNombresSeleccionados : t('reports.context.wholeOrg'),
    rango && (esSemanaEnCurso ? `${rango} ${t('reports.leaders.currentWeek')}` : rango),
    params.fundamentalId && `${t('reports.filters.fundamental')}: ${nombreDeFundamental(params.fundamentalId)}`,
    params.personaId && `${t('reports.filters.person')}: ${resolvePersonName(params.personaId)}`,
  ];
  return partes.filter(Boolean).join(' · ');
}
const NOMBRE_ARCHIVO = 'reporteria-lideres';

/** `canExportData` default `true`: ver nota en `DailyTab.tsx`. */
export function LeadersTab(
  { params, canExportData = true, ouNombresSeleccionados = null }: Readonly<{
    params: RecognitionReportParams;
    canExportData?: boolean;
    /** Nombres de las OUs ELEGIDAS en la barra (no la lista expandida), para el aviso del recorte. */
    ouNombresSeleccionados?: string | null;
  }>,
) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const { resolveOuName } = useOuNameResolver();
  const q = useRecognitionLeadersList(params);
  const rows = q.data?.lideres ?? [];

  // Punto 6 (revisión final) — solo para «Filtros aplicados» del export: el Fundamental de la tira
  // no viaja resuelto en ninguna fila de este listado (a diferencia de `liderNombre`/`ouNombre`).
  const { data: catalog } = useRecognitionCatalog();
  const nombreDeFundamental = useMemo(() => {
    const porId = new Map((catalog ?? []).map((fundamental) => [fundamental.id, fundamental]));
    return (id: string) => {
      const fundamental = porId.get(id);
      return fundamental ? getFundamentalName(fundamental, lang) : id;
    };
  }, [catalog, lang]);
  const { resolvePersonName } = usePersonNameResolver(params.personaId ? [params.personaId] : []);

  // Fase G (Líderes) — REQ-088. El árbol se arma SOLO entre los líderes que trae este listado
  // (`managerId` puede apuntar a alguien fuera de scope o sin equipo propio: esa fila se pinta como
  // raíz de su propia rama). «Nivel» ya no sale de `row.nivel` (el backend nunca lo calcula, ver el
  // comentario de `LeaderAggregateRowDto.nivel`) sino de la profundidad real de este árbol.
  const arbol = useMemo(
    () => buildLeaderForest(rows, (row) => row.liderId, (row) => row.managerId),
    [rows],
  );

  // Paginación estándar: 10 por omisión, escalonado hasta 200. En QA hay 1 629 líderes, así que la
  // tabla venía siendo un scroll infinito. Pagina sobre el árbol YA aplanado en orden padre→hijo, no
  // sobre `rows` crudo, para que el orden en pantalla no salte entre páginas.
  const pag = usePaginacion(arbol);
  const kpis = q.data?.kpis;
  const isEmpty = !!q.data && rows.length === 0;

  /*
   * Un vacío tiene dos causas muy distintas y la pantalla debe separarlas: «no hay líderes en tu
   * alcance» y «la persona que elegiste no tiene gente a cargo». Desde el 2026-09-29 (LID-F3) buscar a
   * alguien deja SOLO su propio equipo (AnalyticsService): quien no es líder da lista vacía y aquí se
   * dice «No se encontraron resultados», en vez de enseñar el equipo de su jefe como dato parcial.
   */
  const vacioPorPersona = isEmpty && Boolean(params.personaId);

  /*
   * Fix final (revisión de rama) — la migración xlsx→exceljs volvió async a `exportMultiSheetExcel`
   * y `exportarLideres` quedó sin try/catch: un fallo (401/500) rechazaba la promesa SIN manejar —
   * cero mensaje, cero log. Mismo hallazgo ya corregido en `handleExportShine` (`ShineTab.tsx`, fix
   * round 1); el mensaje se REUSA de `reports.shine.exporting`/`exportError` (ya genérico) en vez de
   * duplicarlo. Estado ÚNICO para las dos superficies del botón (fila completa y por fila): dos
   * exports en paralelo del mismo dataset no aportan nada y solo invitan a un doble clic confuso.
   */
  const [exportandoLideres, setExportandoLideres] = useState(false);
  const [exportLideresError, setExportLideresError] = useState(false);

  /**
   * Exporta el Excel de Líderes. `soloEstos` acota la hoja de equipos a un subconjunto — es lo que
   * usa el botón por fila de la maqueta.
   *
   * 🔴 El RESUMEN EJECUTIVO conserva los KPIs del recorte COMPLETO aunque se exporte una sola fila, y
   * eso es deliberado: son los KPIs del periodo y del alcance consultado, no de la selección.
   * Recalcularlos sobre una fila daría un «Total de líderes: 1» y un «Ratio promedio» que serían el
   * de esa persona disfrazados de agregado — un número cierto bajo una etiqueta que miente. La hoja
   * de equipos dice qué filas se llevó.
   */
  async function exportarLideres(soloEstos?: LeaderAggregateRowDto[]) {
    if (!q.data) return;
    setExportandoLideres(true);
    setExportLideresError(false);
    try {
      const { kpis: exportKpis } = q.data;
      const lideres = soloEstos ?? q.data.lideres;

      // Las 3 hojas (con su paleta y sus anchos, calcados de `06 - Lideres.xlsx`) se arman en
      // `leadersExcel.ts`; aquí solo se resuelven el periodo, la fecha y los filtros de la tira, que
      // dependen de los hooks de esta vista. `traducir` recibe claves RELATIVAS a `reports.leaders`
      // para que el módulo del Excel no repita ese prefijo en cada una de sus ~50 llamadas.
      const sheets = buildLeadersExcelSheets({
        traducir: (clave: string) => t(`reports.leaders.${clave}`),
        kpis: exportKpis,
        lideres,
        titulo: t('reports.title'),
        subtitulo: t('reports.leaders.subtitle'),
        periodo: [params.startDate, params.endDate].filter(Boolean).join(' - ') || SIN_DATO,
        generado: new Date().toLocaleString(lang),
        filtros: conFiltrosDeTira(
          params.ouIds?.length
            ? `${t('reports.leaders.colOu')}: ${params.ouIds.map(resolveOuName).join(', ')}`
            : SIN_DATO,
          params, nombreDeFundamental, resolvePersonName,
          { fundamental: t('reports.filters.fundamental'), persona: t('reports.filters.person') },
        ),
      });

      await exportarLibro({
        // Una sola fila lleva el nombre de esa persona en el archivo: si no, tres descargas de tres
        // líderes distintos acabarían como `(1)`, `(2)`, `(3)` en la carpeta de descargas y nadie
        // sabría cuál es cuál.
        fileName: soloEstos?.length === 1
          ? `${NOMBRE_ARCHIVO}-${(soloEstos[0].liderNombre ?? soloEstos[0].liderId).replace(/[^\p{L}\p{N}]+/gu, '-')}`
          : NOMBRE_ARCHIVO,
        sheets,
      });

      // Fire-and-forget (mismo patrón que DemograficoExportButton): NUNCA bloquea la descarga, que ya
      // se completó arriba. Si falla, se loguea (guard DEV) — la bitácora perdió UN registro, no la
      // descarga del usuario.
      recognitionReportsApi.auditLeadersListExport(params.ouIds?.join(',') || params.ou, lideres.length).catch((error: unknown) => {
        if (import.meta.env.DEV) console.error('No se pudo registrar la auditoria del export de Lideres:', error);
      });
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('No se pudo generar el export de Lideres:', error);
      setExportLideresError(true);
    } finally {
      setExportandoLideres(false);
    }
  }

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-user-tie" aria-hidden="true" /> {t('reports.leaders.title')}
          </div>
          <div className="au-card-sub">{t('reports.leaders.subtitle')}</div>
        </div>
      </div>

      {/* LID-D2/LID-D1 — FUERA de `ReportStatus`: se lee también con la tabla vacía o cargando. */}
      <div className="rr-filtro-no-aplica" role="status" data-testid="leaders-recorte">
        <i className="fas fa-circle-info" aria-hidden="true" />
        <span>
          <strong>{t('reports.context.showing')}</strong>{' '}
          {textoDelRecorte(params, ouNombresSeleccionados, nombreDeFundamental, resolvePersonName, t)}
          <br />
          {t('reports.leaders.teamSizeNote')}
        </span>
      </div>

      <ReportStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty && !vacioPorPersona}
        onRetry={() => q.refetch()}
      >
        {vacioPorPersona ? (
          <p className="rr-state" role="note">
            <i className="fas fa-circle-info" aria-hidden="true" /> {t('reports.leaders.emptyByPerson')}
          </p>
        ) : (
          <>
            {kpis && (
              <div className="rr-summary-cards">
                <div className="rr-summary-card">
                  <div className="rr-summary-label">{t('reports.leaders.kpiTotalLeaders')}</div>
                  <div className="rr-summary-value" data-testid="leaders-summary-total-value">
                    {kpis.totalLideres}
                  </div>
                </div>
                <div className="rr-summary-card">
                  <div className="rr-summary-label">{t('reports.leaders.kpiTeamSizeSum')}</div>
                  <div className="rr-summary-value" data-testid="leaders-summary-teamsize-value">
                    {kpis.tamanoEquipoSuma}
                  </div>
                </div>
                <div className="rr-summary-card">
                  <div className="rr-summary-label">{t('reports.leaders.kpiRatioAvg')}</div>
                  <div className="rr-summary-value" data-testid="leaders-summary-ratio-value">
                    {kpis.ratioPromedio != null ? kpis.ratioPromedio.toFixed(2) : SIN_DATO}
                  </div>
                </div>
                <div className="rr-summary-card">
                  <div className="rr-summary-label">{t('reports.leaders.kpiParticipationAvg')}</div>
                  <div className="rr-summary-value" data-testid="leaders-summary-participation-value">
                    {`${kpis.participacionPromedio.toFixed(1)}%`}
                  </div>
                </div>
              </div>
            )}

            {/* LID-D3 — mismo bloque que Moderación: `rr-actions--top-gap`, «Exportar a Excel»,
                «Exportando…» y el mismo aviso de error. */}
            {canExportData && (
              <div className="rr-actions rr-actions--top-gap">
                <button
                  type="button"
                  className="rr-btn"
                  onClick={() => exportarLideres()}
                  disabled={rows.length === 0 || exportandoLideres}
                >
                  <i
                    className={exportandoLideres ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}
                    aria-hidden="true"
                  />{' '}
                  {exportandoLideres ? t('reports.shine.exporting') : t('reports.common.exportExcel')}
                </button>
              </div>
            )}

            {/* Mismo hallazgo que `handleExportShine` (fix round 1, `ShineTab.tsx`): sin esto un fallo
                del export quedaba en silencio, sin mensaje ni log. */}
            {exportLideresError && (
              <p className="rr-state rr-state--error" role="alert">
                <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('reports.shine.exportError')}
              </p>
            )}

            <div className="rr-table-wrap">
              <table className="rr-table">
                <thead>
                  <tr>
                    <th>{t('reports.leaders.colLeader')}</th>
                    <th className="rr-num">{t('reports.leaders.colLevel')}</th>
                    <th className="rr-num">{t('reports.leaders.colTeamSize')}</th>
                    <th className="rr-num" title={t('reports.leaders.colTeamReceivedHelp')}>
                      {t('reports.leaders.colTeamReceived')}
                    </th>
                    <th className="rr-num" title={t('reports.leaders.colTeamSentHelp')}>
                      {t('reports.leaders.colTeamSent')}
                    </th>
                    <th className="rr-num">{t('reports.leaders.colParticipation')}</th>
                    {/* La acción va al FINAL, no al principio: es lo convencional para acciones de fila y,
                        sobre todo, no desplaza las columnas de datos que la gente ya sabe dónde buscar. */}
                    {canExportData && <th className="rr-th-actions">{t('reports.leaders.colExport')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {pag.visibles.map(({ row, depth, index }) => (
                    <tr key={row.liderId}>
                      <td style={{ paddingLeft: 10 + depth * 20 }}>
                        <div className="rr-leader-cell">
                          <LeaderAvatar name={row.liderNombre ?? row.liderId} colorIndex={index} />
                          <div className="rr-leader-cell-text">
                            <span className="rr-leader-name">{row.liderNombre ?? row.liderId}</span>
                            <span className="rr-leader-subtitle">
                              {[row.puesto, row.ouNombre].filter(Boolean).join(' · ') || SIN_DATO}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="rr-num">
                        <span className="rr-badge rr-badge--info">{`N-${depth + 1}`}</span>
                      </td>
                      <td className="rr-num">{row.tamanoEquipo}</td>
                      <td className="rr-num">{row.recosEquipoRecibidos}</td>
                      <td className="rr-num">{row.recosEquipoEnviados}</td>
                      <td className="rr-num">{`${row.participacionPct.toFixed(1)}%`}</td>
                      {canExportData && (
                        <td className="rr-td-actions">
                          <button
                            type="button"
                            className="rr-icon-btn"
                            onClick={() => exportarLideres([row])}
                            disabled={exportandoLideres}
                            title={t('reports.leaders.exportRow')}
                            aria-label={t('reports.leaders.exportRowFor', {
                              name: row.liderNombre ?? row.liderId,
                            })}
                          >
                            <i className="fas fa-file-excel" aria-hidden="true" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Paginacion
              pagina={pag.pagina} totalPaginas={pag.totalPaginas} total={pag.total}
              tamano={pag.tamano} onPagina={pag.setPagina} onTamano={pag.cambiarTamano}
            />
          </>
        )}
      </ReportStatus>
    </div>
  );
}
